// import { IJitsiConference } from '../base/conference/reducer';
import { getFakeParticipants } from '../base/participants/functions';
import { toState } from '../base/redux/functions';
import { ALLOW_ALL_URL_DOMAINS, PLAYBACK_START, PLAYBACK_STATUSES, SHARED_VIDEO, VIDEO_PLAYER_PARTICIPANT_NAME, YOUTUBE_PLAYER_PARTICIPANT_NAME, YOUTUBE_URL_DOMAIN } from './constants';
/**
 * Validates the entered video url.
 *
 * It returns a boolean to reflect whether the url matches the youtube regex.
 *
 * @param {string} url - The entered video link.
 * @returns {string} The youtube video id if matched.
 */
function getYoutubeId(url) {
    if (!url) {
        return null;
    }
    const p = /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|(?:m\.)?youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|shorts\/|live\/))((\w|-){11})(?:\S+)?$/;
    const result = url.match(p);
    return result ? result[1] : null;
}
const YOUTUBE_ERROR_DOCS_URL = 'https://developers.google.com/youtube/iframe_api_reference#onError';
const SHARED_VIDEO_ERROR_TITLE_KEY = 'dialog.sharedVideoDialogError';
function _normalizeYoutubeErrorCode(code) {
    return code === 150 ? 101 : code;
}
function _extractYoutubeErrorCode(error) {
    const candidates = [
        error,
        error === null || error === void 0 ? void 0 : error.data,
        error === null || error === void 0 ? void 0 : error.code,
        error === null || error === void 0 ? void 0 : error.error,
        error === null || error === void 0 ? void 0 : error.message
    ];
    for (const candidate of candidates) {
        if (typeof candidate === 'number' && Number.isFinite(candidate)) {
            return candidate;
        }
        if (typeof candidate === 'string') {
            const lowered = candidate.toLowerCase();
            if (lowered.includes('embed') && lowered.includes('allow')) {
                return 101;
            }
            if (lowered.includes('html5')) {
                return 5;
            }
            const match = candidate.match(/\b(2|5|100|101|150|153)\b/);
            if (match) {
                return Number(match[1]);
            }
        }
    }
    return undefined;
}
/**
 * Returns i18n notification props for YouTube/player errors.
 *
 * @param {any} error - Player error payload.
 * @returns {TVideoPlayerErrorNotificationProps}
 */
export function getVideoPlayerErrorNotificationProps(error) {
    const originalCode = _extractYoutubeErrorCode(error);
    if (originalCode === undefined) {
        return {
            titleKey: SHARED_VIDEO_ERROR_TITLE_KEY,
            titleArguments: {
                docsUrl: YOUTUBE_ERROR_DOCS_URL
            },
            descriptionKey: 'dialog.videoPlayerErrorDefault',
        };
    }
    const normalizedCode = _normalizeYoutubeErrorCode(originalCode);
    const titleArguments = {
        code: originalCode,
        docsUrl: YOUTUBE_ERROR_DOCS_URL
    };
    if (normalizedCode === 2) {
        return {
            titleKey: SHARED_VIDEO_ERROR_TITLE_KEY,
            titleArguments,
            descriptionKey: 'dialog.youtubePlayerErrorInvalidParameter'
        };
    }
    if (normalizedCode === 5) {
        return {
            titleKey: SHARED_VIDEO_ERROR_TITLE_KEY,
            titleArguments,
            descriptionKey: 'dialog.youtubePlayerErrorHtml5'
        };
    }
    if (normalizedCode === 100) {
        return {
            titleKey: SHARED_VIDEO_ERROR_TITLE_KEY,
            titleArguments,
            descriptionKey: 'dialog.youtubePlayerErrorNotFound'
        };
    }
    if (normalizedCode === 101) {
        return {
            titleKey: SHARED_VIDEO_ERROR_TITLE_KEY,
            titleArguments,
            descriptionKey: 'dialog.youtubePlayerErrorEmbedNotAllowed'
        };
    }
    if (normalizedCode === 153) {
        return {
            titleKey: SHARED_VIDEO_ERROR_TITLE_KEY,
            titleArguments,
            descriptionKey: 'dialog.youtubePlayerErrorRefererMissing'
        };
    }
    return {
        titleKey: SHARED_VIDEO_ERROR_TITLE_KEY,
        titleArguments,
        descriptionKey: 'dialog.youtubePlayerErrorUnknown'
    };
}
/**
 * Checks if the status is one that is actually sharing the video - playing, pause or start.
 *
 * @param {string} status - The shared video status.
 * @returns {boolean}
 */
export function isSharingStatus(status) {
    return [PLAYBACK_STATUSES.PLAYING, PLAYBACK_STATUSES.PAUSED, PLAYBACK_START].includes(status);
}
/**
 * Returns true if there is a video being shared in the meeting.
 *
 * @param {Object | Function} stateful - The Redux state or a function that gets resolved to the Redux state.
 * @returns {boolean}
 */
export function isVideoPlaying(stateful) {
    let videoPlaying = false;
    const fakeParticipants = getFakeParticipants(stateful);
    for (const p of fakeParticipants) {
        if ((p === null || p === void 0 ? void 0 : p.name) === VIDEO_PLAYER_PARTICIPANT_NAME || (p === null || p === void 0 ? void 0 : p.name) === YOUTUBE_PLAYER_PARTICIPANT_NAME) {
            videoPlaying = true;
            break;
        }
    }
    return videoPlaying;
}
/**
 * Returns true if YouTube is being shared in the meeting.
 *
 * @param {Object | Function} stateful - The Redux state or a function that gets resolved to the Redux state.
 * @returns {boolean}
 */
export function isYouTubePlaying(stateful) {
    let youtubePlaying = false;
    const fakeParticipants = getFakeParticipants(stateful);
    for (const p of fakeParticipants) {
        if ((p === null || p === void 0 ? void 0 : p.name) === YOUTUBE_PLAYER_PARTICIPANT_NAME) {
            youtubePlaying = true;
            break;
        }
    }
    return youtubePlaying;
}
/**
 * Extracts a Youtube id or URL from the user input.
 *
 * @param {string} input - The user input.
 * @returns {string|undefined}
 */
export function extractYoutubeIdOrURL(input) {
    if (!input) {
        return;
    }
    const trimmedLink = input.trim();
    if (!trimmedLink) {
        return;
    }
    const youtubeId = getYoutubeId(trimmedLink);
    if (youtubeId) {
        return youtubeId;
    }
    // Check if the URL is valid, native may crash otherwise.
    try {
        new URL(trimmedLink);
    }
    catch (_) {
        return;
    }
    return trimmedLink;
}
/**
 * Returns true if shared video functionality is enabled and false otherwise.
 *
 * @param {IStateful} stateful - - The redux store or {@code getState} function.
 * @returns {boolean}
 */
export function isSharedVideoEnabled(stateful) {
    const state = toState(stateful);
    const { disableThirdPartyRequests = false } = state['features/base/config'];
    return !disableThirdPartyRequests;
}
/**
 * Returns true if the passed url is allowed to be used for shared video or not.
 *
 * @param {string} url - The URL.
 * @param {Array<string>} allowedUrlDomains - The allowed url domains.
 * @param {boolean} considerNonURLsAllowedForYoututbe - If true, the invalid URLs will be considered youtube IDs
 * and if youtube is allowed the function will return true.
 * @returns {boolean}
 */
export function isURLAllowedForSharedVideo(url, allowedUrlDomains = [], considerNonURLsAllowedForYoututbe = false) {
    var _a;
    if (!url) {
        return false;
    }
    try {
        const urlObject = new URL(url);
        if (['http:', 'https:'].includes((_a = urlObject === null || urlObject === void 0 ? void 0 : urlObject.protocol) === null || _a === void 0 ? void 0 : _a.toLowerCase())) {
            return allowedUrlDomains.includes(ALLOW_ALL_URL_DOMAINS) || allowedUrlDomains.includes(urlObject === null || urlObject === void 0 ? void 0 : urlObject.hostname);
        }
    }
    catch (_e) { // it should be YouTube id.
        return considerNonURLsAllowedForYoututbe && allowedUrlDomains.includes(YOUTUBE_URL_DOMAIN);
    }
    return false;
}
/**
 * Sends SHARED_VIDEO command.
 *
 * @param {string} id - The id of the video.
 * @param {string} status - The status of the shared video.
 * @param {JitsiConference} conference - The current conference.
 * @param {string} localParticipantId - The id of the local participant.
 * @param {string} time - The seek position of the video.
 * @returns {void}
 */
export function sendShareVideoCommand({ id, status, conference, localParticipantId = '', time, muted, volume }) {
    conference === null || conference === void 0 ? void 0 : conference.sendCommandOnce(SHARED_VIDEO, {
        value: id,
        attributes: {
            from: localParticipantId,
            muted,
            state: status,
            time,
            volume
        }
    });
}
